import { Component, OnDestroy, OnInit } from '@angular/core';
import { NbMediaBreakpointsService, NbMenuService, NbSidebarService, NbThemeService, NbToastrService } from '@nebular/theme';

import { UserData } from '../../../@core/data/users';
import { LayoutService } from '../../../@core/utils';
import { map, take, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import Swal from 'sweetalert2';
import { environment } from '../../../../environments/environment';

@Component({
  standalone: false,
  selector: 'ngx-header',
  styleUrls: ['./header.component.scss'],
  templateUrl: './header.component.html',
})
export class HeaderComponent implements OnInit, OnDestroy {

  private destroy$: Subject<void> = new Subject<void>();
  userPictureOnly: boolean = false;
  user: any;
  tokenCopyCount: number = 0;

  themes = [
    {
      value: 'default',
      name: 'Light',
    },
    {
      value: 'dark',
      name: 'Dark',
    },
  ];

  currentTheme = 'default';

  userMenu = [{ title: 'Profile' }, { title: 'Log out' }];
  userData;

  expiryTimeStamp: string = '';
  nbfTimeStamp: string = '';

  private readonly expiryCheckIntervalMs = 60 * 1000;

  constructor(private sidebarService: NbSidebarService,
    private oidcSecurityService: OidcSecurityService,
    private menuService: NbMenuService,
    private themeService: NbThemeService,
    private userService: UserData,
    private layoutService: LayoutService,
    private breakpointService: NbMediaBreakpointsService,
    private toastrService: NbToastrService
  ) {
  }

  ngOnInit() {

    this.checkExpiryAndRefresh();

    setTimeout(() => {
      this.checkExpiryAndRefresh();
    }, this.expiryCheckIntervalMs);

    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      this.themeService.changeTheme('dark');
      this.currentTheme = 'dark';
    }

    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      if (e.matches) {
        this.themeService.changeTheme('dark');
        this.currentTheme = 'dark';
      } else {
        this.themeService.changeTheme('default');
        this.currentTheme = 'default';
      }
    });

    this.userService.getUsers()
      .pipe(takeUntil(this.destroy$))
      .subscribe((users: any) => this.user = users.nick);

    const { xl } = this.breakpointService.getBreakpointsMap();
    this.themeService.onMediaQueryChange()
      .pipe(
        map(([, currentBreakpoint]) => currentBreakpoint.width < xl),
        takeUntil(this.destroy$),
      )
      .subscribe((isLessThanXl: boolean) => this.userPictureOnly = isLessThanXl);

    this.themeService.onThemeChange()
      .pipe(
        map(({ name }) => name),
        takeUntil(this.destroy$),
      );

    this.menuService.onItemClick().subscribe((event) => {
      if (window.innerWidth < 1200) {
        this.toggleSidebar();
      }
    });

    this.oidcSecurityService.userData$.subscribe({
      next: (userData) => {
        if (userData && userData.userData) {
          this.userData = userData.userData;
        }
      }
    })
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  changeTheme(themeName: string) {
    this.themeService.changeTheme(themeName);
  }

  toggleSidebar(): boolean {
    this.sidebarService.toggle(true, 'menu-sidebar');
    this.layoutService.changeLayoutSize();

    return false;
  }

  navigateHome() {
    this.menuService.navigateHome();
    return false;
  }

  logout() {
    Swal.fire({
      title: 'Wirklich ausloggen?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Ja',
      cancelButtonText: 'Nein'
    }).then((result) => {
      if (result.isConfirmed) {
        this.oidcSecurityService.logoff().subscribe({
          next: () => {
            console.log('Logged out');
          },
          error: (error) => {
            console.log(error);
          }
        });
      }
    })
  }

    checkExpiryAndRefresh(force: boolean = false) {
    // Check the duration of the current session
    this.oidcSecurityService.getAccessToken().pipe(take(1)).subscribe({
      next: (token) => {
        if (!token) {
          return;
        }

        // parse token
        let exp: number | undefined;
        let nbf: number | undefined;
        try {
          const payload = JSON.parse(
            atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')),
          );
          exp = payload.exp;
          nbf = payload.nbf;
        } catch {
          return;
        }

        if (!exp) {
          return;
        }

        const nowSeconds = Math.floor(Date.now() / 1000);
        const renewBeforeSeconds = 1800;

        this.expiryTimeStamp = new Date(exp * 1000).toLocaleString('de-DE', { hour: '2-digit', minute: '2-digit' });
        this.nbfTimeStamp = new Date(nbf * 1000).toLocaleString('de-DE', { hour: '2-digit', minute: '2-digit' });

        if (exp - nowSeconds <= renewBeforeSeconds || force) {
          console.log('Token expires in less than 30 minutes, refreshing...');
          this.oidcSecurityService.forceRefreshSession().pipe(take(1)).subscribe({
            next: (loginResponse) => {

              this.checkExpiryAndRefresh();
              this.toastrService.success('Session erfolgreich erneuert', 'Erfolgreich');

              if (!loginResponse.isAuthenticated) {
                sessionStorage.clear();
                this.oidcSecurityService.logoffAndRevokeTokens();
                this.oidcSecurityService.authorize();
                return;
              }
            },
            error: () => {
              sessionStorage.clear();
              this.oidcSecurityService.logoffAndRevokeTokens();
              this.oidcSecurityService.authorize();
            },
          });
          return;
        }
      },
      error: () => {
        console.error('Error checking expiry');
      },
    });
  }

  token(): string {
    let accessToken = '';
    this.oidcSecurityService.getAccessToken().pipe(take(1)).subscribe(token => {
      accessToken = token;
    })
    return accessToken;
  }

  copyToken() {
    this.tokenCopyCount++;
    if (this.tokenCopyCount > 2) {
      navigator.clipboard.writeText(this.token());
      this.toastrService.success('Aktuellen Access Token in Zwischenablage kopiert', 'Erfolgreich');
      this.tokenCopyCount = 0;
    }
  }
}
